import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {API} from '../../apis/api';
import {buildErrorMessage} from '../../apis/calls';
import {AggregateDispatch, TabbedViewWithDateSelect} from '../../components';
import {
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  DATE_FORMAT,
} from '../../constants';
import {useAjaxCallbackWithIntervals} from '../../hooks';
import {usePopupManager} from '../../providers/PopupManager/PopupManager';
import {usePlantAndLinesStore} from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import {statusStringMap} from '../../staticData';
import { getDatesForRange } from '../../utils/utils';

const AggregateDispatchView = ({aggregate}) => {
  const {showInternalError, showError} = usePopupManager();

  const [plant] = usePlantAndLinesStore().plantState;
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;

  const [agsnCount, setAgsnCount] = useState(0);
  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [handleDispatchEdit, setHandleDispatchEdit] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());

  const [lastAgsnGenerationDate, setLastAgsnGenerationDate] = useState(null);
  const [agsnDates, setAgsnDates] = useState([]);

  const loadUsableLastAgsnGenerationDate = async (plantId, lineId) => {
    const today = moment();
    try {
      const response = await API.get(
        API_RESOURCE_URLS.lastAgSNGeneratedDate(plantId, lineId),
      );
      const {data} = response;
      const lastAgsnGenerationDateFromResponse = data.date;
      if (
        lastAgsnGenerationDateFromResponse &&
        moment(lastAgsnGenerationDateFromResponse).isSameOrAfter(today, 'day')
      ) {
        setLastAgsnGenerationDate(moment(lastAgsnGenerationDateFromResponse));
      } else {
        setLastAgsnGenerationDate(null);
      }
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Fetch Last AGSN Date`,
        error: error,
      });
    }
  };

  const fetchAggregatesRowsDataForDate = async (plantId, lineId, dateString) =>
    API.get(API_RESOURCE_URLS.agsn(plantId, lineId), {
      params: {
        date: dateString,
        status: [AGGREGATE_STATUS.DISPATCHED, AGGREGATE_STATUS.BOOKED],
      },
    });

  const fetchPreviousAggregatesRowsData = async (plantId, lineId, date) =>
    API.get(API_RESOURCE_URLS.agsn(plantId, lineId), {
      params: {
        before: date.format(DATE_FORMAT.ISO),
        status: [AGGREGATE_STATUS.BOOKED],
      },
    });

  const getAggregatesDataBasedOnDate = async (plantId, lineId, date) => {
    const today = moment();
    const dateString = date.format(DATE_FORMAT.ISO);
    if (date.isBefore(today, 'day'))
      return fetchPreviousAggregatesRowsData(plantId, lineId, today);
    return fetchAggregatesRowsDataForDate(plantId, lineId, dateString);
  };

  const loadAggregateAgsnForDate = async (plantId, lineId, date) => {
    if (!plantId) return;
    if (!lineId) return;
    if (!date) return;

    try {
      setRowsData(prevState => ({...prevState, loading: true}));
      const response = await getAggregatesDataBasedOnDate(
        plantId,
        lineId,
        date,
      );

      const {data} = response;

      const agsnsWithStatusMapping = data.map(item => ({
        ...item,
        aggregateStatus: statusStringMap[item.status],
        vcStatus: statusStringMap[item.parentStatus],
      }));

      setRowsData({
        loading: false,
        data: [...agsnsWithStatusMapping],
        filteredData: [...agsnsWithStatusMapping],
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      setRowsData(prevState => ({...prevState, data: [], loading: false}));
      showInternalError();
    }
  };

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    rowsData.loading,
    handleDispatchEdit,
    loadAggregateAgsnForDate,
    plant?.value,
    aggregateLine?.value,
    selectedDate,
  );

  useEffect(() => {
    if (plant?.value && aggregateLine?.value)
      loadUsableLastAgsnGenerationDate(plant.value, aggregateLine.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, aggregateLine]);

  useEffect(() => {
    if (lastAgsnGenerationDate) {
      setSelectedDate(moment());
      setAgsnDates(getDatesForRange(moment(), lastAgsnGenerationDate))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAgsnGenerationDate?.format(DATE_FORMAT.ISO)]);

  return (
    <TabbedViewWithDateSelect
      tabs={[
        {
          label: 'Dispatch',
          title: 'Dispatch',
          count: agsnCount,
          component: AggregateDispatch,
          props: {
            aggregate,
            setAgsnCount,
            rowsData,
            lastAgsnGenerationDate,
            lastRefreshedDate,
            selectedDate,
            setSelectedDate,
            loadAggregateAgsnForDate,
            handleDispatchEdit,
            setHandleDispatchEdit,
            agsnDates
          },
        },
      ]}
    />
  );
};

AggregateDispatchView.propTypes = {
  aggregate: PropTypes.string.isRequired
}

export default AggregateDispatchView;
